import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Header, API, Footer } from "../App";
import { useWidgetMode } from "@/lib/widgetMode";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Check, AlertCircle } from "lucide-react";
import axios from "axios";

const CONDITION_QUESTIONS = [
  {
    id: "exterior_condition",
    question: "What is the exterior condition of your vehicle?",
    options: [
      { value: "excellent", label: "Excellent", description: "No visible damage, scratches, or dents", adjustment: 500 },
      { value: "good", label: "Good", description: "Minor scratches or small dings", adjustment: 0 },
      { value: "fair", label: "Fair", description: "Noticeable scratches, dents, or paint issues", adjustment: -800 },
      { value: "poor", label: "Poor", description: "Significant body damage or rust", adjustment: -2000 }
    ]
  },
  {
    id: "interior_condition",
    question: "What is the interior condition?",
    options: [
      { value: "excellent", label: "Excellent", description: "Like new, no wear or stains", adjustment: 400 },
      { value: "good", label: "Good", description: "Normal wear for age, clean", adjustment: 0 },
      { value: "fair", label: "Fair", description: "Visible stains, tears, or odors", adjustment: -600 },
      { value: "poor", label: "Poor", description: "Heavy damage, missing parts", adjustment: -1500 }
    ]
  },
  {
    id: "mechanical_issues",
    question: "Are there any mechanical issues?",
    options: [
      { value: "none", label: "None", description: "Runs perfectly, no warning lights", adjustment: 300 },
      { value: "minor", label: "Minor Issues", description: "Warning lights or minor issues", adjustment: -500 },
      { value: "major", label: "Major Issues", description: "Engine, transmission, or drivetrain problems", adjustment: -3000 }
    ]
  },
  {
    id: "tire_condition",
    question: "What is the tire condition?",
    options: [
      { value: "new", label: "New/Like New", description: "80%+ tread remaining", adjustment: 200 },
      { value: "good", label: "Good", description: "50-80% tread remaining", adjustment: 0 },
      { value: "worn", label: "Worn", description: "Needs replacement soon", adjustment: -400 }
    ]
  },
  {
    id: "title_status",
    question: "What is the title status?",
    options: [
      { value: "clean", label: "Clean Title", description: "No accidents or damage reported", adjustment: 0 },
      { value: "rebuilt", label: "Rebuilt/Reconstructed", description: "Previously salvaged, now rebuilt", adjustment: -3000 },
      { value: "salvage", label: "Salvage Title", description: "Total loss or severe damage history", adjustment: -5000 }
    ]
  },
  {
    id: "keys_and_books",
    question: "Do you have all keys and owner's manual?",
    options: [
      { value: "all", label: "All Keys + Manual", description: "Complete set with documentation", adjustment: 100 },
      { value: "keys_only", label: "Keys Only", description: "All keys but no manual", adjustment: 0 },
      { value: "one_key", label: "One Key", description: "Only one key, no spare", adjustment: -200 },
      { value: "no_keys", label: "No Keys", description: "Keys are missing", adjustment: -500 }
    ]
  }
];

const ConditionScreen = ({ state, updateState }) => {
  const navigate = useNavigate();
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(false);
  const advanceTimeoutRef = useRef(null);
  const { isCompact: compact } = useWidgetMode();

  useEffect(() => {
    if (!state.offer) {
      navigate('/');
    }
  }, [state.offer, navigate]);

  useEffect(() => {
    return () => clearTimeout(advanceTimeoutRef.current);
  }, []);

  const question = CONDITION_QUESTIONS[currentQuestion];
  const isLastQuestion = currentQuestion === CONDITION_QUESTIONS.length - 1;
  const selectedAnswer = answers[question?.id];

  const advance = (currentAnswers) => {
    if (isLastQuestion) {
      const conditionAnswers = Object.entries(currentAnswers).map(([questionId, answer]) => ({
        question_id: questionId,
        answer: answer.value,
        adjustment: answer.adjustment
      }));

      updateState({ conditionAnswers });
      navigate('/photos');
    } else {
      setCurrentQuestion(prev => prev + 1);
    }
  };

  const handleSelect = (value, adjustment) => {
    clearTimeout(advanceTimeoutRef.current);

    const nextAnswers = {
      ...answers,
      [question.id]: { value, adjustment }
    };
    setAnswers(nextAnswers);
    advanceTimeoutRef.current = setTimeout(() => advance(nextAnswers), 450);
  };

  const handleBack = () => {
    clearTimeout(advanceTimeoutRef.current);

    if (currentQuestion === 0) {
      navigate('/instant-offer');
    } else {
      setCurrentQuestion(prev => prev - 1);
    }
  };

  const totalAdjustment = Object.values(answers).reduce((sum, a) => sum + a.adjustment, 0);

  if (!state.offer || !question) return null;
  const fieldHeight = compact ? "h-11" : "h-14";

  return (
    <div className={compact ? "flex flex-col bg-white" : "min-h-screen flex flex-col bg-white"}>
      <Header showProgress currentStep={4} variant="light" />

      <main className={compact ? "py-4" : "min-h-[calc(100vh-80px)] py-12 sm:py-20"}>
        <div className="container-flow">
          {/* Header */}
          <div className={`text-center opacity-0 animate-fade-in-up ${compact ? "mb-4" : "mb-8"}`}>
            <span className={`text-label text-blue-600 block ${compact ? "mb-1" : "mb-4"}`}>
              QUESTION {currentQuestion + 1} OF {CONDITION_QUESTIONS.length}
            </span>
            <h1 className={compact ? "text-xl font-medium tracking-tight text-slate-900 mb-1" : "text-heading-2 mb-2"}>
              Vehicle Condition
            </h1>
            <p className={compact ? "text-sm text-slate-500" : "text-body"}>
              Your answers help us provide an accurate locked offer.
            </p>
          </div>

          {/* Progress Bar */}
          <div className={`w-full bg-slate-200 rounded-full h-2 ${compact ? "mb-4" : "mb-8"}`}>
            <div
              className="bg-blue-600 h-2 rounded-full transition-all duration-300"
              style={{ width: `${((currentQuestion + 1) / CONDITION_QUESTIONS.length) * 100}%` }}
            />
          </div>

          {/* Question Card */}
          <div key={question.id} className={`surface-card opacity-0 animate-fade-in-up animate-stagger-2 ${compact ? "p-3" : "p-8 sm:p-10"}`}>
            <h2 className={compact ? "text-base font-semibold text-slate-900 mb-3" : "text-xl font-semibold text-slate-900 mb-6"} data-testid="condition-question">
              {question.question}
            </h2>

            {/* Options */}
            <div className={compact ? "space-y-2" : "space-y-3"}>
              {question.options.map((option) => {
                const isSelected = selectedAnswer?.value === option.value;
                return (
                  <button
                    key={option.value}
                    onClick={() => handleSelect(option.value, option.adjustment)}
                    data-testid={`condition-option-${option.value}`}
                    className={`w-full text-left condition-card ${compact ? "p-3" : ""} ${isSelected ? 'selected' : ''}`}
                  >
                    <div className={`flex items-start ${compact ? "gap-3" : "gap-4"}`}>
                      <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-0.5
                        ${isSelected ? 'border-blue-600 bg-blue-600' : 'border-slate-300'}`}
                      >
                        {isSelected && <Check className="w-4 h-4 text-white" />}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between">
                          <span className="font-medium text-slate-900">{option.label}</span>
                          {option.adjustment !== 0 && (
                            <span className={`text-sm font-medium ${option.adjustment > 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                              {option.adjustment > 0 ? '+' : ''}{option.adjustment.toLocaleString()}
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-slate-500 mt-0.5">{option.description}</p>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Running Total */}
          {Object.keys(answers).length > 0 && (
            <div className={`${compact ? "p-3" : "p-4"} bg-slate-100 rounded-xl flex items-center justify-between opacity-0 animate-fade-in-up animate-stagger-3 ${compact ? "mt-3" : "mt-6"}`}>
              <span className="text-sm text-slate-600">Running adjustment:</span>
              <span className={`font-semibold ${totalAdjustment >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                {totalAdjustment >= 0 ? '+' : ''}{totalAdjustment.toLocaleString()}
              </span>
            </div>
          )}

          {/* Actions */}
          <div className={`opacity-0 animate-fade-in-up animate-stagger-4 ${compact ? "mt-4" : "mt-8"}`}>
            <Button
              variant="outline"
              onClick={handleBack}
              className={`${fieldHeight} btn-secondary`}
              data-testid="back-btn"
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              Back
            </Button>
          </div>
        </div>
      </main>
      {!compact && <Footer minimal />}
    </div>
  );
};

export default ConditionScreen;
